import React, { useState } from 'react';
import { toast } from 'react-toastify';
import api from '../../services/api';

const AgingReport = () => {
  const [agingTypes, setAgingTypes] = useState({ AR: true, AP: true, DC: true });
  const [asOf, setAsOf] = useState(new Date().toISOString().split('T')[0]);
  const [groupBy, setGroupBy] = useState('party');
  const [sortBy, setSortBy] = useState('name');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const toggleType = (t) => setAgingTypes(prev => ({ ...prev, [t]: !prev[t] }));

  const handleSearch = async () => {
    const types = Object.entries(agingTypes).filter(([, v]) => v).map(([k]) => k).join(',');
    if (!types) { toast.info('Please select at least one aging report type'); return; }

    setLoading(true);
    try {
      const res = await api.get(`/accounting-reports/aging?types=${types}&as_of=${asOf}&group_by=${groupBy}&sort_by=${sortBy}`);
      setData(res.data);
    } catch (error) {
      toast.error('Failed to load aging report');
    } finally {
      setLoading(false);
    }
  };

  const fmt = (v) => {
    const n = Number(v || 0);
    if (n === 0) return '';
    return n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const fmtTotal = (v) => Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const checkboxStyle = { marginRight: '4px' };
  const filterLabel = { width: '140px', fontSize: '13px', fontWeight: 600, flexShrink: 0 };
  const filterRow = { display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' };

  return (
    <div className="card" style={{ padding: '24px' }}>
      {/* Header */}
      <div style={{ background: '#5f9ea0', color: '#fff', padding: '10px 16px', borderRadius: '4px', marginBottom: '16px' }}>
        <h2 style={{ margin: 0, fontSize: '16px', fontWeight: 700 }}>Aging Report</h2>
      </div>

      {/* Filters */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '20px' }}>
        <div>
          <div style={filterRow}>
            <span style={filterLabel}>*Aging Report Type</span>
            <label style={{ fontSize: '13px' }}><input type="checkbox" checked={agingTypes.AR} onChange={() => toggleType('AR')} style={checkboxStyle} />A/R</label>
            <label style={{ fontSize: '13px' }}><input type="checkbox" checked={agingTypes.AP} onChange={() => toggleType('AP')} style={checkboxStyle} />A/P</label>
            <label style={{ fontSize: '13px' }}><input type="checkbox" checked={agingTypes.DC} onChange={() => toggleType('DC')} style={checkboxStyle} />D/C</label>
          </div>
          <div style={filterRow}>
            <span style={filterLabel}>*Ending Date</span>
            <input type="date" value={asOf} onChange={(e) => setAsOf(e.target.value)}
              style={{ padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px' }} />
          </div>
        </div>
        <div>
          <div style={filterRow}>
            <span style={filterLabel}>Group by</span>
            <label style={{ fontSize: '13px' }}><input type="radio" checked={groupBy === 'party'} onChange={() => setGroupBy('party')} style={checkboxStyle} />Trade Partner</label>
            <label style={{ fontSize: '13px' }}><input type="radio" checked={groupBy === 'invoice'} onChange={() => setGroupBy('invoice')} style={checkboxStyle} />Invoice</label>
          </div>
          <div style={filterRow}>
            <span style={filterLabel}>Sort by</span>
            <label style={{ fontSize: '13px' }}><input type="radio" checked={sortBy === 'name'} onChange={() => setSortBy('name')} style={checkboxStyle} />Name</label>
            <label style={{ fontSize: '13px' }}><input type="radio" checked={sortBy === 'balance'} onChange={() => setSortBy('balance')} style={checkboxStyle} />Balance</label>
          </div>
        </div>
      </div>

      <div style={{ display: 'flex', gap: '8px', marginBottom: '20px', justifyContent: 'center' }}>
        <button onClick={handleSearch} disabled={loading}
          style={{ padding: '8px 24px', backgroundColor: '#3b82f6', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 600 }}>
          {loading ? 'Loading...' : 'Search'}
        </button>
      </div>

      {/* Results */}
      {data && (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', fontSize: '12px', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#f1f5f9', borderBottom: '2px solid #e2e8f0' }}>
                <th style={{ padding: '8px', textAlign: 'left', minWidth: '180px' }}>Name</th>
                {groupBy === 'party' && <th style={{ padding: '8px', textAlign: 'right' }}>Average Aging</th>}
                <th style={{ padding: '8px', textAlign: 'right' }}>Current Balance</th>
                {groupBy === 'party' && <th style={{ padding: '8px', textAlign: 'right' }}>Current Count</th>}
                <th style={{ padding: '8px', textAlign: 'right' }}>Past Due Balance</th>
                {groupBy === 'party' && <th style={{ padding: '8px', textAlign: 'right' }}>Past Due Count</th>}
                <th style={{ padding: '8px', textAlign: 'right' }}>Over 1-30 Days</th>
                <th style={{ padding: '8px', textAlign: 'right' }}>Over 31-60 Days</th>
                <th style={{ padding: '8px', textAlign: 'right' }}>Over 61-90 Days</th>
                <th style={{ padding: '8px', textAlign: 'right' }}>Over 90 Days</th>
                <th style={{ padding: '8px', textAlign: 'right', fontWeight: 700 }}>Balance Amount</th>
              </tr>
            </thead>
            <tbody>
              {data.data.length === 0 ? (
                <tr><td colSpan={11} style={{ padding: '20px', textAlign: 'center', color: '#94a3b8' }}>No data found</td></tr>
              ) : groupBy === 'party' ? (
                data.data.map((row, idx) => (
                  <tr key={idx} style={{ borderBottom: '1px solid #f1f5f9' }}>
                    <td style={{ padding: '6px 8px', fontWeight: 500 }}>{row.name}</td>
                    <td style={{ padding: '6px 8px', textAlign: 'right' }}>{row.average_aging}</td>
                    <td style={{ padding: '6px 8px', textAlign: 'right' }}>{fmt(row.current)}</td>
                    <td style={{ padding: '6px 8px', textAlign: 'right' }}>{row.current_count || ''}</td>
                    <td style={{ padding: '6px 8px', textAlign: 'right' }}>{fmt(row.past_due_balance)}</td>
                    <td style={{ padding: '6px 8px', textAlign: 'right' }}>{row.past_due_count || ''}</td>
                    <td style={{ padding: '6px 8px', textAlign: 'right' }}>{fmt(row.over_1_30)}</td>
                    <td style={{ padding: '6px 8px', textAlign: 'right' }}>{fmt(row.over_31_60)}</td>
                    <td style={{ padding: '6px 8px', textAlign: 'right' }}>{fmt(row.over_61_90)}</td>
                    <td style={{ padding: '6px 8px', textAlign: 'right' }}>{fmt(row.over_90)}</td>
                    <td style={{ padding: '6px 8px', textAlign: 'right', fontWeight: 600 }}>{fmtTotal(row.balance)}</td>
                  </tr>
                ))
              ) : (
                data.data.map((row, idx) => (
                  <tr key={idx} style={{ borderBottom: '1px solid #f1f5f9' }}>
                    <td style={{ padding: '6px 8px' }}>{row.party_name} - {row.invoice_no || 'N/A'}</td>
                    <td style={{ padding: '6px 8px', textAlign: 'right' }}>{fmt(row.current)}</td>
                    <td style={{ padding: '6px 8px', textAlign: 'right' }}>{fmt(row.balance - row.current)}</td>
                    <td style={{ padding: '6px 8px', textAlign: 'right' }}>{fmt(row.over_1_30)}</td>
                    <td style={{ padding: '6px 8px', textAlign: 'right' }}>{fmt(row.over_31_60)}</td>
                    <td style={{ padding: '6px 8px', textAlign: 'right' }}>{fmt(row.over_61_90)}</td>
                    <td style={{ padding: '6px 8px', textAlign: 'right' }}>{fmt(row.over_90)}</td>
                    <td style={{ padding: '6px 8px', textAlign: 'right', fontWeight: 600 }}>{fmtTotal(row.balance)}</td>
                  </tr>
                ))
              )}
            </tbody>
            {/* Totals */}
            <tfoot>
              <tr style={{ borderTop: '2px solid #e2e8f0', fontWeight: 700, background: '#f8fafc' }}>
                <td style={{ padding: '8px' }}>Total</td>
                {groupBy === 'party' && <td></td>}
                <td style={{ padding: '8px', textAlign: 'right' }}>{fmtTotal(data.totals.current)}</td>
                {groupBy === 'party' && <td></td>}
                <td style={{ padding: '8px', textAlign: 'right' }}>{fmtTotal(data.totals.over_1_30 + data.totals.over_31_60 + data.totals.over_61_90 + data.totals.over_90)}</td>
                {groupBy === 'party' && <td></td>}
                <td style={{ padding: '8px', textAlign: 'right' }}>{fmtTotal(data.totals.over_1_30)}</td>
                <td style={{ padding: '8px', textAlign: 'right' }}>{fmtTotal(data.totals.over_31_60)}</td>
                <td style={{ padding: '8px', textAlign: 'right' }}>{fmtTotal(data.totals.over_61_90)}</td>
                <td style={{ padding: '8px', textAlign: 'right' }}>{fmtTotal(data.totals.over_90)}</td>
                <td style={{ padding: '8px', textAlign: 'right', color: '#2563eb' }}>{fmtTotal(data.totals.balance)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </div>
  );
};

export default AgingReport;
